import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { EmptyState } from "../components/EmptyState";
import { SidebarPanel } from "../components/SidebarPanel";
import { Button } from "../components/Button";
import { SelectInput } from "../components/SelectInput";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import {
  useGetUserQuery,
  useGetStoreUsersQuery,
  useRemoveStoreUserMutation,
  useUpdateStoreUserMutation,
} from "../query/usersQuery";
import { useGetPermissionGroupsQuery } from "../query/permissionGroupsQuery";
import { useGetStoreQuery } from "../query/storesQuery";
import { useGetActiveFeaturesQuery } from "../query/billingItemsQuery";
import { UserForm } from "./components/UserForm";
import { ChangePasswordForm } from "./components/ChangePasswordForm";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { UserRole } from "@KassaroAB/shared/types/enums/UserRole";
import type { ChainId, PermissionGroupId, StoreId, UserId } from "@KassaroAB/shared/types/ids";
import type { DBUser } from "@KassaroAB/shared/types/user";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";

const ActionsCell = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
  align-items: center;
`;

const SmallButton = styled(Button)`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const DangerButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const PermissionGroupSelect = styled(SelectInput)`
  min-width: 160px;
  font-size: ${fontSizes.small}px;

  & > button {
    padding: ${spacing.x1}px ${spacing.x3}px ${spacing.x1}px ${spacing.x1}px;
  }
`;

const canAssignPermissionGroup = (role: UserRole) =>
  role !== UserRole.ADMIN && role !== UserRole.RESELLER_ADMIN;

const canChangePassword = (currentRole: UserRole | undefined, role: UserRole) =>
  currentRole === UserRole.ADMIN ||
  (currentRole === UserRole.RESELLER_ADMIN && role === UserRole.USER);

const getUserAccessLabel = (
  user: DBUser,
  storeId: StoreId,
  chainId: ChainId | null | undefined,
  t: (key: string) => string,
) => {
  if (chainId && user.chainIds?.includes(chainId)) {
    return t("usersView.accessChain");
  }
  if (user.storeIds?.includes(storeId)) {
    return t("usersView.accessStore");
  }
  return "-";
};

export const UsersView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordUser, setPasswordUser] = useState<{
    id: UserId;
    email: string;
  } | null>(null);

  const { data: currentUser } = useGetUserQuery();

  const { data: store } = useGetStoreQuery(storeId);
  const { data: activeFeatures } = useGetActiveFeaturesQuery(storeId ?? null);
  const showPermissionGroups = activeFeatures?.includes(Features.STAFF) ?? false;

  const {
    data: users,
    isLoading,
    error,
  } = useGetStoreUsersQuery(storeId ?? null);

  const { data: permissionGroups } = useGetPermissionGroupsQuery(
    showPermissionGroups ? (store?.chainId ?? null) : null,
  );

  const { mutate: removeUser, error: removeUserError, isPending: isRemovePending } =
    useRemoveStoreUserMutation(storeId ?? null);
  const { mutate: updateStoreUser, error: updateStoreUserError, isPending: isUpdatePending } =
    useUpdateStoreUserMutation(storeId ?? null);

  const permissionGroupNames = useMemo(() => {
    const names = new Map<PermissionGroupId, string>();
    permissionGroups?.forEach((group) => {
      names.set(group.id, group.name);
    });
    return names;
  }, [permissionGroups]);

  const handleAddUserSuccess = () => {
    setSuccessMessage(t("usersView.successAdded"));
    setIsPanelOpen(false);
  };

  const handleRemoveUser = (userId: string, userEmail: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("usersView.removeConfirm", { email: userEmail }))) {
      removeUser(userId, {
        onSuccess: () => {
          setSuccessMessage(t("usersView.successRemoved"));
        },
        onError: () => {
          setSuccessMessage(null);
        },
      });
    }
  };

  const handlePermissionGroupChange = (
    userId: string,
    permissionGroup: PermissionGroupId,
  ) => {
    updateStoreUser(
      {
        userId,
        data: { permissionGroup },
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("usersView.successPermissionGroupUpdated"));
        },
      },
    );
  };

  const handlePasswordChanged = () => {
    setSuccessMessage(t("usersView.successPasswordChanged"));
    setPasswordUser(null);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoading) {
    return (
      <PageContent title={t("usersView.title")}>
        <LoadingContainer message={t("usersView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("usersView.title")}
      actions={<Button onClick={() => setIsPanelOpen(true)}>{t("usersView.addUser")}</Button>}
    >
      {error && (
        <InfoBox variant="error">
          {error?.message ?? t("usersView.errorLoad")}
        </InfoBox>
      )}

      {removeUserError && (
        <InfoBox variant="error">
          {removeUserError?.message ?? t("usersView.errorRemove")}
        </InfoBox>
      )}

      {updateStoreUserError && (
        <InfoBox variant="error">
          {updateStoreUserError?.message ??
            t("usersView.errorPermissionGroupUpdate")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {users && users.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("usersView.tableEmail")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableRole")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableAccess")}</TableHeaderCell>
                {showPermissionGroups && (
                  <TableHeaderCell>{t("usersView.tablePermissionGroup")}</TableHeaderCell>
                )}
                <TableHeaderCell>{t("usersView.tableCreated")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableActions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell noWrap>{user.email || "-"}</TableCell>
                  <TableCell noWrap>{user.name || "-"}</TableCell>
                  <TableCell noWrap>{user.role || "-"}</TableCell>
                  <TableCell noWrap>
                    {getUserAccessLabel(user, storeId!, store?.chainId, t)}
                  </TableCell>
                  {showPermissionGroups && (
                    <TableCell noWrap>
                      {canAssignPermissionGroup(user.role) && permissionGroups ? (
                        <PermissionGroupSelect
                          value={user.permissionGroup ?? ""}
                          disabled={isUpdatePending}
                          onChange={(e) =>
                            handlePermissionGroupChange(
                              user.id,
                              e.target.value as PermissionGroupId,
                            )
                          }
                        >
                          <option value="" disabled>
                            {t("usersView.selectPermissionGroup")}
                          </option>
                          {permissionGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </PermissionGroupSelect>
                      ) : (
                        user.permissionGroup
                          ? permissionGroupNames.get(user.permissionGroup) ??
                            user.permissionGroup
                          : "-"
                      )}
                    </TableCell>
                  )}
                  <TableCell noWrap>{formatDate(user.createdAt)}</TableCell>
                  <TableCell noWrap>
                    <ActionsCell>
                      {canChangePassword(currentUser?.role, user.role) && (
                        <SmallButton
                          $color="secondary"
                          onClick={() =>
                            setPasswordUser({ id: user.id, email: user.email })
                          }
                        >
                          {t("usersView.changePassword")}
                        </SmallButton>
                      )}
                      <DangerButton
                        onClick={(e) =>
                          handleRemoveUser(user.id, user.email || t("usersView.thisUser"), e)
                        }
                        disabled={isRemovePending}
                      >
                        {t("usersView.remove")}
                      </DangerButton>
                    </ActionsCell>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState message={t("usersView.empty")} />
        </Card>
      )}

      <SidebarPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        title={t("usersView.panelAddTitle")}
      >
        <UserForm
          storeId={storeId ?? null}
          chainId={store?.chainId ?? null}
          showPermissionGroups={showPermissionGroups}
          onSuccess={handleAddUserSuccess}
          onCancel={handlePanelClose}
        />
      </SidebarPanel>

      <SidebarPanel
        isOpen={!!passwordUser}
        onClose={() => setPasswordUser(null)}
        title={t("usersView.panelChangePasswordTitle")}
      >
        {passwordUser && (
          <ChangePasswordForm
            key={passwordUser.id}
            storeId={storeId ?? null}
            userId={passwordUser.id}
            email={passwordUser.email}
            onSuccess={handlePasswordChanged}
            onCancel={() => setPasswordUser(null)}
          />
        )}
      </SidebarPanel>
    </PageContent>
  );
};
